import { z } from 'zod';

import type { TFunction } from 'i18next';

import { stripHtml } from '@/helpers';

export const CARD_NAME_MAX_LENGTH = 120;

export const CARD_SUBJECT_TYPE_STAFF = 'Staff' as const;
export const CARD_SUBJECT_TYPE_PERMISSION_GROUP = 'PermissionGroup' as const;

export const CARD_SUBJECT_TYPES = [
  CARD_SUBJECT_TYPE_STAFF,
  CARD_SUBJECT_TYPE_PERMISSION_GROUP,
] as const;

// Polymorphic class names — must match Catalog::CardDiscount::TARGET_TYPES.
export const CARD_DISCOUNT_TARGET_SERVICE = 'Catalog::Service' as const;
export const CARD_DISCOUNT_TARGET_SERVICE_FOLDER =
  'Catalog::ServiceCategory' as const;

export const CARD_DISCOUNT_TARGET_TYPES = [
  CARD_DISCOUNT_TARGET_SERVICE,
  CARD_DISCOUNT_TARGET_SERVICE_FOLDER,
] as const;

// Online-sale kinds — must match the `kind` enum on Catalog::Card.
export const CARD_KIND_REGULAR = 'regular' as const;
export const CARD_KIND_SUBSCRIPTION = 'subscription' as const;

export const CARD_KINDS = [CARD_KIND_REGULAR, CARD_KIND_SUBSCRIPTION] as const;

export const SUBSCRIPTION_DURATION_IN_DAYS = 31;

const MIN_PERCENT = 0;
const MAX_PERCENT = 100;

const isNonNegativeNumber = (value: string) =>
  value.trim() !== '' && !isNaN(Number(value)) && Number(value) >= 0;

const isNonNegativeInteger = (value: string) =>
  value.trim() !== '' && Number.isInteger(Number(value)) && Number(value) >= 0;

const isPositiveInteger = (value: string) =>
  value.trim() !== '' && Number.isInteger(Number(value)) && Number(value) > 0;

const createSubjectRowSchema = (t: TFunction) =>
  z.object({
    record_id: z.number().nullable(),
    subject_type: z.enum(CARD_SUBJECT_TYPES),
    subject_id: z.string().min(1, { message: t('validation_required') }),
  });

const createDiscountRowSchema = (t: TFunction) =>
  z.object({
    record_id: z.number().nullable(),
    target_type: z.enum(CARD_DISCOUNT_TARGET_TYPES),
    target_id: z.string().min(1, { message: t('validation_required') }),
    discount_percent: z
      .string()
      .refine(
        (value) =>
          value.trim() !== '' &&
          !isNaN(Number(value)) &&
          Number(value) >= MIN_PERCENT &&
          Number(value) <= MAX_PERCENT,
        { message: t('validation_invalid') },
      ),
    max_uses: z
      .string()
      .refine((value) => value.trim() === '' || isPositiveInteger(value), {
        message: t('validation_invalid'),
      }),
  });

const createTimeWindowRowSchema = (t: TFunction) =>
  z
    .object({
      record_id: z.number().nullable(),
      weekday: z.number().int().min(0).max(6),
      start_time: z.string().min(1, { message: t('validation_required') }),
      end_time: z.string().min(1, { message: t('validation_required') }),
    })
    .refine((window) => window.end_time > window.start_time, {
      message: t('Card time window end after start'),
      path: ['end_time'],
    });

const createCardServiceSchema = (t: TFunction) =>
  z
    .object({
      record_id: z.number().nullable(),
      // '' = no primary service.
      service_id: z.string(),
      is_unlimited: z.boolean(),
      visits_count: z.string(),
    })
    .refine(
      (service) =>
        service.service_id === '' ||
        service.is_unlimited ||
        isPositiveInteger(service.visits_count),
      { message: t('validation_invalid'), path: ['visits_count'] },
    );

export const createCatalogCardFormSchema = (t: TFunction) =>
  z
    .object({
      card_category_id: z
        .string()
        .min(1, { message: t('validation_required') }),
      name: z
        .string()
        .trim()
        .min(1, { message: t('validation_required') })
        .max(CARD_NAME_MAX_LENGTH, {
          message: t('validation_max_length', { count: CARD_NAME_MAX_LENGTH }),
        }),
      // Optional client-facing override of `name`.
      display_name: z
        .string()
        .trim()
        .max(CARD_NAME_MAX_LENGTH, {
          message: t('validation_max_length', { count: CARD_NAME_MAX_LENGTH }),
        }),
      receipt_name: z
        .string()
        .trim()
        .max(CARD_NAME_MAX_LENGTH, {
          message: t('validation_max_length', { count: CARD_NAME_MAX_LENGTH }),
        }),
      description: z
        .string()
        .optional()
        .transform((value) =>
          value && stripHtml(value).trim().length > 0 ? value : '',
        ),
      business_direction_id: z
        .string()
        .min(1, { message: t('validation_required') }),
      price: z
        .string()
        .refine(isNonNegativeNumber, { message: t('validation_invalid') }),
      is_perpetual: z.boolean(),
      duration_in_days: z.string(),
      max_freeze_days: z
        .string()
        .refine((value) => value.trim() === '' || isPositiveInteger(value), {
          message: t('validation_invalid'),
        }),
      max_activation_days: z
        .string()
        .refine((value) => value.trim() === '' || isNonNegativeInteger(value), {
          message: t('validation_invalid'),
        }),
      // Optional note attached to the auto-created price-change log on update.
      reason: z.string().optional(),
      location_ids: z
        .array(z.string())
        .min(1, { message: t('validation_required') }),
      card_service: createCardServiceSchema(t),
      card_discounts: z.array(createDiscountRowSchema(t)),
      card_active_time_windows: z.array(createTimeWindowRowSchema(t)),
      card_selling_permissions: z.array(createSubjectRowSchema(t)),
      available_online: z.boolean(),
      kind: z.string(),
      subscription_start_service_id: z.string().nullable(),
      subscription_resubscribe_days: z.string(),
    })
    .superRefine((values, ctx) => {
      // A non-perpetual card needs a concrete validity period.
      if (!values.is_perpetual && !isPositiveInteger(values.duration_in_days)) {
        ctx.addIssue({
          code: 'custom',
          message: t('validation_required'),
          path: ['duration_in_days'],
        });
      }

      const hasPrimaryService = values.card_service.service_id !== '';
      const hasDiscounts = values.card_discounts.length > 0;

      // Backend: a card must have either a primary service or ≥1 discount.
      if (!hasPrimaryService && !hasDiscounts) {
        ctx.addIssue({
          code: 'custom',
          message: t('Card must grant something'),
          path: ['card_service', 'service_id'],
        });
      }

      // Backend: unlimited visits require a fixed validity period (not perpetual).
      if (
        hasPrimaryService &&
        values.card_service.is_unlimited &&
        values.is_perpetual
      ) {
        ctx.addIssue({
          code: 'custom',
          message: t('Unlimited card needs duration'),
          path: ['is_perpetual'],
        });
      }

      // Backend: a discount cannot target the card's primary service.
      if (hasPrimaryService) {
        values.card_discounts.forEach((discount, index) => {
          if (
            discount.target_type === CARD_DISCOUNT_TARGET_SERVICE &&
            discount.target_id === values.card_service.service_id
          ) {
            ctx.addIssue({
              code: 'custom',
              message: t('Card discount cannot target primary service'),
              path: ['card_discounts', index, 'target_id'],
            });
          }
        });
      }

      if (values.kind === CARD_KIND_SUBSCRIPTION) {
        if (values.is_perpetual) {
          ctx.addIssue({
            code: 'custom',
            message: t('Card subscription duration'),
            path: ['is_perpetual'],
          });
        } else if (
          Number(values.duration_in_days) !== SUBSCRIPTION_DURATION_IN_DAYS
        ) {
          ctx.addIssue({
            code: 'custom',
            message: t('Card subscription duration'),
            path: ['duration_in_days'],
          });
        }
      }

      // Online-sale configuration is validated only when the card is offered online.
      if (values.available_online) {
        if (values.kind === '') {
          ctx.addIssue({
            code: 'custom',
            message: t('validation_required'),
            path: ['kind'],
          });
        }

        if (
          values.kind === CARD_KIND_SUBSCRIPTION &&
          !isNonNegativeInteger(values.subscription_resubscribe_days)
        ) {
          ctx.addIssue({
            code: 'custom',
            message: t('validation_required'),
            path: ['subscription_resubscribe_days'],
          });
        }
      }
    });

export type CatalogCardSubjectRow = z.infer<
  ReturnType<typeof createSubjectRowSchema>
>;

export type CatalogCardDiscountRow = z.infer<
  ReturnType<typeof createDiscountRowSchema>
>;

export type CatalogCardTimeWindowRow = z.infer<
  ReturnType<typeof createTimeWindowRowSchema>
>;

export type CatalogCardFormValues = z.infer<
  ReturnType<typeof createCatalogCardFormSchema>
>;
